/**
 * App-level Copilot Studio session state.
 * Lives in a provider so Direct Line conversation survives route changes
 * (chat panel unmounts when switching layouts).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  copilotService,
  type CopilotDirectLineToken,
  type CopilotHistoryMessage,
} from '@services/copilot.service';
import { chatService, type ActiveSessionResponse } from '@services/chat.service';
import { useAuthStore } from '@store/auth.store';

const mapActiveSessionToHistory = (
  session: ActiveSessionResponse | null
): CopilotHistoryMessage[] => {
  if (!session?.messages?.length) {
    return [];
  }

  return session.messages.map((msg) => ({
    id: msg.id,
    role: msg.role,
    content: msg.content,
    activity_id: msg.activity_id ?? null,
    created_at: msg.created_at,
  }));
};

export interface UseCopilotStudioResult {
  enabled: boolean;
  configured: boolean;
  loading: boolean;
  sessionReady: boolean;
  token: string | null;
  conversationId: string | null;
  directLineDomain: string | null;
  streamUrl: string | null;
  error: string | null;
  sessionKey: number;
  reconnected: boolean;
  historyMessages: CopilotHistoryMessage[];
  /** Loads config, Direct Line token, and active session history once per login */
  initialize: () => Promise<void>;
  /** Ensures token + history are ready (e.g. when opening the panel after refresh) */
  ensureSession: () => Promise<void>;
  refreshToken: (newChat?: boolean) => Promise<void>;
  startNewChat: () => Promise<void>;
}

const CopilotStudioContext = createContext<UseCopilotStudioResult | undefined>(undefined);

export const CopilotStudioProvider = ({ children }: { children: ReactNode }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const accessToken = useAuthStore((state) => state.accessToken);

  const [enabled, setEnabled] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [directLineDomain, setDirectLineDomain] = useState<string | null>(null);
  const [streamUrl, setStreamUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessionKey, setSessionKey] = useState(0);
  const [reconnected, setReconnected] = useState(false);
  const [historyMessages, setHistoryMessages] = useState<CopilotHistoryMessage[]>([]);

  const tokenRef = useRef<string | null>(null);
  const conversationIdRef = useRef<string | null>(null);
  const historyMessagesRef = useRef<CopilotHistoryMessage[]>([]);
  const initInFlightRef = useRef(false);
  const initializedRef = useRef(false);

  tokenRef.current = token;
  conversationIdRef.current = conversationId;
  historyMessagesRef.current = historyMessages;

  const sessionReady = Boolean(enabled && token && conversationId);

  const applyTokenResponse = useCallback((tokenResponse: CopilotDirectLineToken) => {
    setToken(tokenResponse.token);
    setConversationId(tokenResponse.conversation_id);
    setDirectLineDomain(tokenResponse.directline_domain);
    setStreamUrl(tokenResponse.stream_url || null);
    setReconnected(Boolean(tokenResponse.reconnected));
    if (tokenResponse.history_messages?.length) {
      setHistoryMessages(tokenResponse.history_messages);
    }
  }, []);

  const loadHistoryFromActiveSession = useCallback(async () => {
    const session = await chatService.getActiveSession('copilot');
    const history = mapActiveSessionToHistory(session);
    if (history.length > 0) {
      setHistoryMessages(history);
    }
    return session;
  }, []);

  const syncHistoryAfterToken = useCallback(
    async (tokenResponse: CopilotDirectLineToken) => {
      if (tokenResponse.history_messages?.length) {
        return;
      }
      await loadHistoryFromActiveSession();
    },
    [loadHistoryFromActiveSession]
  );

  const clearSession = useCallback(() => {
    setToken(null);
    setConversationId(null);
    setStreamUrl(null);
    setReconnected(false);
    setHistoryMessages([]);
    tokenRef.current = null;
    conversationIdRef.current = null;
  }, []);

  const refreshToken = useCallback(async (newChat = false) => {
    if (!isAuthenticated || !accessToken) {
      setError('Sign in to use AI Assistant');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const tokenResponse = await copilotService.getDirectLineToken(newChat);
      applyTokenResponse(tokenResponse);
      if (!newChat) {
        await syncHistoryAfterToken(tokenResponse);
      }
      setEnabled(true);
    } catch (err: unknown) {
      const axiosDetail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      const message =
        axiosDetail ||
        (err instanceof Error ? err.message : 'Failed to connect to AI Assistant');
      setError(message);
      clearSession();
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [applyTokenResponse, clearSession, isAuthenticated, accessToken, syncHistoryAfterToken]);

  const initialize = useCallback(async () => {
    if (!isAuthenticated || !accessToken) {
      setLoading(false);
      return;
    }

    const hasExistingSession = Boolean(tokenRef.current && conversationIdRef.current);

    if (hasExistingSession) {
      setEnabled(true);
      if (historyMessagesRef.current.length === 0) {
        await loadHistoryFromActiveSession();
      }
      setLoading(false);
      return;
    }

    if (initInFlightRef.current) {
      return;
    }

    initInFlightRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const config = await copilotService.getConfig();
      setConfigured(config.configured);
      setDirectLineDomain((current) => current || config.directline_domain || null);

      if (!config.enabled) {
        setEnabled(false);
        initializedRef.current = true;
        return;
      }

      const tokenResponse = await copilotService.getDirectLineToken();
      applyTokenResponse(tokenResponse);
      await syncHistoryAfterToken(tokenResponse);
      setEnabled(true);
      initializedRef.current = true;
    } catch (err: unknown) {
      const axiosDetail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      const message =
        axiosDetail ||
        (err instanceof Error ? err.message : 'Failed to load AI Assistant');
      setError(message);
      setEnabled(false);
      clearSession();
    } finally {
      initInFlightRef.current = false;
      setLoading(false);
    }
  }, [applyTokenResponse, clearSession, isAuthenticated, accessToken, loadHistoryFromActiveSession, syncHistoryAfterToken]);

  const ensureSession = useCallback(async () => {
    if (!isAuthenticated || !accessToken) {
      return;
    }

    if (initInFlightRef.current) {
      return;
    }

    if (tokenRef.current && conversationIdRef.current) {
      // A session is already live in memory (Direct Line connection + webchat state
      // persist for the page's lifetime) — just make sure the panel renders it.
      // Do NOT re-fetch history or reconnect here: a message sent moments ago may not
      // have finished persisting to the DB yet, so a "no messages found" read would
      // wrongly trigger a reconnect that can reassign the conversation and discard
      // the still-live, already-working chat view.
      setEnabled(true);
      return;
    }

    if (!initializedRef.current) {
      await initialize();
      return;
    }

    initializedRef.current = false;
    await initialize();
  }, [
    applyTokenResponse,
    initialize,
    isAuthenticated,
    accessToken,
    loadHistoryFromActiveSession,
    syncHistoryAfterToken,
  ]);

  const startNewChat = useCallback(async () => {
    if (!isAuthenticated || !accessToken) {
      return;
    }

    setLoading(true);
    setError(null);
    setHistoryMessages([]);
    setReconnected(false);
    setToken(null);
    setConversationId(null);
    tokenRef.current = null;
    conversationIdRef.current = null;

    try {
      const tokenResponse = await copilotService.getDirectLineToken(true);
      setSessionKey((key) => key + 1);
      applyTokenResponse(tokenResponse);
      setHistoryMessages([]);
      setEnabled(true);
    } catch (err: unknown) {
      const axiosDetail =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { detail?: string } } }).response?.data?.detail
          : undefined;
      const message =
        axiosDetail ||
        (err instanceof Error ? err.message : 'Failed to start new Copilot chat');
      setError(message);
      clearSession();
      setEnabled(false);
    } finally {
      setLoading(false);
    }
  }, [applyTokenResponse, clearSession, isAuthenticated, accessToken]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      initializedRef.current = false;
      clearSession();
      setEnabled(false);
      setConfigured(false);
      setError(null);
      setLoading(false);
      initInFlightRef.current = false;
      return;
    }

    if (initializedRef.current || initInFlightRef.current) {
      return;
    }

    void initialize();
  }, [isAuthenticated, accessToken, clearSession, initialize]);

  const value = useMemo(
    () => ({
      enabled,
      configured,
      loading,
      sessionReady,
      token,
      conversationId,
      directLineDomain,
      streamUrl,
      error,
      sessionKey,
      reconnected,
      historyMessages,
      initialize,
      ensureSession,
      refreshToken,
      startNewChat,
    }),
    [
      enabled,
      configured,
      loading,
      sessionReady,
      token,
      conversationId,
      directLineDomain,
      streamUrl,
      error,
      sessionKey,
      reconnected,
      historyMessages,
      initialize,
      ensureSession,
      refreshToken,
      startNewChat,
    ]
  );

  return (
    <CopilotStudioContext.Provider value={value}>{children}</CopilotStudioContext.Provider>
  );
};

export const useCopilotStudio = (): UseCopilotStudioResult => {
  const context = useContext(CopilotStudioContext);
  if (!context) {
    throw new Error('useCopilotStudio must be used within CopilotStudioProvider');
  }
  return context;
};
